import React, { useState, useCallback } from 'react';
import { useTranslation } from 'localization';
import StarPickerAtom from '../../atoms/marketplace/StarPickerAtom';

const MAX_REVIEW_LENGTH = 2000;

export interface RatingFormSubmitData {
  stars: number;
  reviewTitle?: string;
  reviewBody?: string;
}

export interface RatingFormMoleculeProps {
  initialStars?: number;
  initialTitle?: string;
  initialBody?: string;
  onSubmit: (data: RatingFormSubmitData) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isUpdate?: boolean;
  className?: string;
}

const RatingFormMolecule: React.FC<RatingFormMoleculeProps> = ({
  initialStars = 0,
  initialTitle = '',
  initialBody = '',
  onSubmit,
  onCancel,
  isSubmitting = false,
  isUpdate = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [stars, setStars] = useState(initialStars);
  const [reviewTitle, setReviewTitle] = useState(initialTitle);
  const [reviewBody, setReviewBody] = useState(initialBody);
  const [starsError, setStarsError] = useState(false);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (stars === 0) {
        setStarsError(true);
        return;
      }
      setStarsError(false);
      onSubmit({
        stars,
        reviewTitle: reviewTitle.trim() || undefined,
        reviewBody: reviewBody.trim() || undefined,
      });
    },
    [stars, reviewTitle, reviewBody, onSubmit],
  );

  const handleStarsChange = useCallback((value: number) => {
    setStars(value);
    setStarsError(false);
  }, []);

  const classes = ['marketplace-rating-form', className].filter(Boolean).join(' ');

  const submitLabel = isUpdate
    ? t('marketplace.ratings.updateRating')
    : t('marketplace.ratings.submitRating');

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <div className="marketplace-rating-form__stars-field">
        <label className="marketplace-rating-form__label">
          {t('marketplace.ratings.yourRating')}
          <span className="marketplace-rating-form__required">*</span>
        </label>
        <StarPickerAtom
          value={stars}
          onChange={handleStarsChange}
          size="large"
          disabled={isSubmitting}
        />
        {starsError && (
          <span className="marketplace-rating-form__error">
            {t('marketplace.ratings.starsRequired')}
          </span>
        )}
      </div>

      <div className="marketplace-rating-form__field">
        <label className="marketplace-rating-form__label" htmlFor="rating-title">
          {t('marketplace.reviews.titlePlaceholder')}
        </label>
        <input
          id="rating-title"
          type="text"
          className="marketplace-rating-form__input Search-input-box"
          value={reviewTitle}
          onChange={(e) => setReviewTitle(e.target.value)}
          placeholder={t('marketplace.reviews.titlePlaceholder')}
          disabled={isSubmitting}
          maxLength={255}
        />
      </div>

      <div className="marketplace-rating-form__field">
        <label className="marketplace-rating-form__label" htmlFor="rating-body">
          {t('marketplace.reviews.bodyPlaceholder')}
        </label>
        <textarea
          id="rating-body"
          className="marketplace-rating-form__textarea Search-input-box"
          value={reviewBody}
          onChange={(e) => setReviewBody(e.target.value)}
          placeholder={t('marketplace.reviews.bodyPlaceholder')}
          disabled={isSubmitting}
          maxLength={MAX_REVIEW_LENGTH}
          rows={4}
        />
        <span className="marketplace-rating-form__char-count">
          {t('marketplace.reviews.bodyMaxLength', {
            count: reviewBody.length,
            max: MAX_REVIEW_LENGTH,
          })}
        </span>
      </div>

      <div className="marketplace-rating-form__actions">
        <button
          type="submit"
          className="Save-button"
          disabled={isSubmitting || stars === 0}
        >
          {isSubmitting && <span className="marketplace-rating-form__spinner" />}
          {submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            className="Close-button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common.actions.cancel')}
          </button>
        )}
      </div>
    </form>
  );
};

export default RatingFormMolecule;
